/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Inject, Injectable, OnDestroy } from '@angular/core';
import { appendElementToHead, createStyleElement, destroyElement } from '@gh/core-util';
import { flatten } from 'lodash';
import { PAGE_ENVIRONMENT_STYLE_FACTORIES, PageEnvironment } from './page-environment.interfaces';

@Injectable()
export class PageEnvironmentManager implements OnDestroy {
  private environments: PageEnvironment[] = [];
  private styleEl: HTMLElement;
  private currentEnvNames: string[] = [];

  constructor(@Inject(PAGE_ENVIRONMENT_STYLE_FACTORIES) private styleFactories: Hash<TransformFn<PageEnvironment, string>>) {
    this.styleEl = createStyleElement();
    appendElementToHead(this.styleEl);
    this.update();
  }

  ngOnDestroy(): void {
    destroyElement(this.styleEl);
  }

  use(env: PageEnvironment): void {
    this.environments.push(env);
    this.update();
  }

  unuse(): void {
    this.environments.pop();
    this.update();
  }

  update(): void {
    this.updateDocument();
  }

  private updateDocument(): void {
    const { documentElement } = document;
    const { environments, currentEnvNames } = this;

    if (documentElement) {
      if (currentEnvNames.length > 0) {
        documentElement.classList.remove(...this.currentEnvNames.map((envName) => `env-${envName}`));
      }

      if (environments.length > 0) {
        const nextEnvNames = environments.map((env) => env.name);
        this.currentEnvNames = nextEnvNames;
        documentElement.classList.add(...nextEnvNames.map((envName) => `env-${envName}`));
      }

      this.styleEl.textContent = flatten(environments.map((env) => {
        const factory = this.styleFactories[env.name];
        return factory ? factory(env) : '';
      })).join('\n');
    }
  }
}
